import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Emotes, PlayerEmoteSlug, socketKeys } from "../../types/SharedTypes";
import { useSocket } from "../../contexts/SocketContext";
import { EmoteIcon } from "../../assets/icons";
import { socket } from "../../socket";

export const EmoteButton = ({setEmotesVisible}: {setEmotesVisible: Dispatch<SetStateAction<boolean>>}) => {

  const {user} = useSocket();
  const [emote, setEmote] = useState<PlayerEmoteSlug | null>(null);

  useEffect(() => {
  
      const handleEmote = ({userId, emote}: {userId: string, emote: PlayerEmoteSlug}) => {
        if (user?.id === userId) setEmote(emote);
      }
  
      socket.on(socketKeys.toClient.game.playerEmote, handleEmote)
      return () => {
        socket.off(socketKeys.toClient.game.playerEmote, handleEmote);
      }
    }, []);

  return (
    <div className="position-absolute right bottom padding">
      {emote &&
        <div
          className='item-list center-horizontal emote-pop full-width small'
          onAnimationEnd={() => setEmote(null)}
        >
          <div className="badge tag" data-small={true} data-white={true}>
            <p className="subheading-small cap">Sent</p>
          </div>
          <div className='user-avatar small allow-overflow'>
            <img src={`/img/emotes/${emote}.png`} />
          </div>
        </div>
      }
      <button className="icon large no-padding shadow" onClick={() => setEmotesVisible(prev => !prev)}>
        <EmoteIcon />
      </button>
    </div>
  )
}

const EmoteWheel = ({hideWheel}: {hideWheel: () => void}) => {

  const {sendEmote} = useSocket();
  const emotes = Object.values(Emotes);

  const handleSendEmote = (i: number) => {
    hideWheel();
    const emote = emotes?.[i];
    if (!emote) return;

    sendEmote(emote);
  }

  return (
    <div className="position-absolute emote-wheel" onClick={hideWheel}>
      <div className="emote-wheel-inner">
        <RingMenu
          segments={emotes.length}
          imgUrls={emotes.map(slug => `/img/emotes/${slug}.png`)}
          onSelect={handleSendEmote}
        />
        <p className="heading-medium outline position-absolute">Emotes</p>
      </div>
    </div>
  )
};

type RingMenuProps = {
  segments: number;
  innerRadius?: number;
  outerRadius?: number;
  onSelect?: (index: number) => void;
  imgUrls?: string[]
};

const SIZE = 100;

const polarToCartesian = (
  cx: number,
  cy: number,
  r: number,
  angleDeg: number
) => {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  };
};

function describeArcSegment(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startAngle: number,
  endAngle: number
) {
  const outerStart = polarToCartesian(cx, cy, outerR, endAngle);
  const outerEnd = polarToCartesian(cx, cy, outerR, startAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, startAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, endAngle);

  const largeArc = endAngle - startAngle <= 180 ? "0" : "1";

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 0 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 1 ${innerEnd.x} ${innerEnd.y}`,
    "Z",
  ].join(" ");
}

const pointOnCircle = (
  cx: number,
  cy: number,
  r: number,
  angleDeg: number
) => {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  };
};

export const RingMenu: React.FC<RingMenuProps> = ({
  segments,
  innerRadius = 24,
  outerRadius = 48,
  onSelect,
  imgUrls
}) => {
  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const angleStep = 360 / segments;

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      width="100%"
      height="100%"
    >
      {Array.from({ length: segments }).map((_, i) => {
        const start = i * angleStep;
        const end = start + angleStep;

        const midAngle = start + angleStep / 2;
        const midRadius = (innerRadius + outerRadius) / 2;

        const pos = pointOnCircle(cx, cy, midRadius, midAngle);
        const boxSize = 14;

        const path = describeArcSegment(
          cx,
          cy,
          innerRadius,
          outerRadius,
          start,
          end
        );

        return (
          <g key={i}>
            <path
              key={i}
              d={path}
              onClick={(e) => {e.stopPropagation(); onSelect?.(i)}}
            />
            {imgUrls?.[i] &&
              <foreignObject
                x={pos.x - boxSize / 2}
                y={pos.y - boxSize / 2}
                width={boxSize}
                height={boxSize}
                style={{ pointerEvents: "none" }}
              >
                <img src={imgUrls[i]} style={{width: boxSize}} />
              </foreignObject>
            }
          </g>
        );
      })}
    </svg>
  );
};


export default EmoteWheel;